import { Socket } from 'socket.io-client';
import { AudioQualityMonitor, AudioQualityMetrics, ConnectionStats } from './audioQuality';

export interface WebRTCConfig {
  iceServers: RTCIceServer[];
  iceCandidatePoolSize?: number;
  bundlePolicy?: RTCBundlePolicy;
  rtcpMuxPolicy?: RTCRtcpMuxPolicy;
  iceTransportPolicy?: RTCIceTransportPolicy;
  iceConnectionReceivingTimeout?: number;
  iceBackupCandidatePairPingInterval?: number;
}

export interface VoiceChatState {
  isConnected: boolean;
  isMuted: boolean;
  isSpeaking: boolean;
  connectionQuality: 'excellent' | 'good' | 'poor' | 'disconnected';
}

export class WebRTCManager {
  private peerConnection: RTCPeerConnection | null = null;
  private localStream: MediaStream | null = null;
  private remoteStream: MediaStream | null = null;
  private socket: Socket | null = null;
  private currentUserId: string | null = null;
  private targetUserId: string | null = null;
  private roomId: string | null = null;
  private audioQualityMonitor: AudioQualityMonitor | null = null;
  private audioLevelMonitor: (() => void) | null = null;
  private pendingIceCandidates: RTCIceCandidateInit[] | null = null;
  
  // コールバック関数
  private onConnectionStateChange?: (state: VoiceChatState) => void;
  private onRemoteStream?: (stream: MediaStream) => void;
  private onAudioStateChange?: (isMuted: boolean) => void;
  private onQualityChange?: (quality: AudioQualityMetrics) => void;
  private onConnectionStats?: (stats: ConnectionStats) => void;

  private readonly config: WebRTCConfig = {
    iceServers: [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' },
      { urls: 'stun:stun2.l.google.com:19302' },
      { urls: 'stun:stun3.l.google.com:19302' },
      { urls: 'stun:stun4.l.google.com:19302' },
      // 追加のSTUNサーバー（接続安定性向上）
      { urls: 'stun:stun.stunprotocol.org:3478' },
      { urls: 'stun:stun.voiparound.com' },
      { urls: 'stun:stun.voipbuster.com' },
      { urls: 'stun:stun.voipstunt.com' },
      { urls: 'stun:stun.counterpath.com' },
    ],
    // 接続安定性を大幅向上させる設定
    iceCandidatePoolSize: 20, // より多くの候補を事前収集
    bundlePolicy: 'max-bundle',
    rtcpMuxPolicy: 'require',
    // 追加の安定性設定
    iceTransportPolicy: 'all', // 全てのICE候補を使用
    iceConnectionReceivingTimeout: 10000, // 10秒でタイムアウト
    iceBackupCandidatePairPingInterval: 2500 // バックアップ候補のping間隔
  };

  constructor(socket: Socket, currentUserId: string) {
    this.socket = socket;
    this.currentUserId = currentUserId;
    this.setupSocketListeners();
  }

  private setupSocketListeners(): void {
    if (!this.socket) {
      return;
    }

    // WebRTC シグナリングイベント
    this.socket.on('webrtc-offer', async (data: { fromUserId: string; offer: RTCSessionDescriptionInit }) => {
      await this.handleOffer(data.offer, data.fromUserId);
    });

    this.socket.on('webrtc-answer', async (data: { fromUserId: string; answer: RTCSessionDescriptionInit }) => {
      await this.handleAnswer(data.answer);
    });

    this.socket.on('webrtc-ice-candidate', async (data: { fromUserId: string; candidate: RTCIceCandidateInit }) => {
      await this.handleIceCandidate(data.candidate);
    });

    // ボイスチャット管理イベント
    this.socket.on('voice-chat-started', (data: { roomId: string; matchedUserId: string; autoStart?: boolean }) => {
      this.roomId = data.roomId;
      this.targetUserId = data.matchedUserId;
      console.log('ボイスチャット開始通知受信:', data.autoStart ? '自動開始' : '手動開始');
      this.startVoiceChat();
    });

    this.socket.on('voice-chat-ended', (data: { roomId: string }) => {
      this.endVoiceChat();
    });

    this.socket.on('audio-state-change', (data: { fromUserId: string; isMuted: boolean }) => {
      this.onAudioStateChange?.(data.isMuted);
    });
  }

  async initialize(): Promise<boolean> {
    try {
      // HTTPSチェック（モバイルブラウザ対応）
      if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
        throw new Error('HTTPSが必要です。モバイルブラウザではHTTPS接続でのみマイクアクセスが許可されます。');
      }

      // モバイルブラウザでの制約チェック
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('このブラウザはマイクアクセスに対応していません。');
      }

      console.log('マイクアクセスを要求しています...');

      // 音声品質を最適化した制約を使用
      const constraints = {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          // 接続安定性を重視した設定
          sampleRate: 48000, // 高品質
          channelCount: 1,
          // 帯域幅制限を緩和
          latency: 0.1, // 低遅延
        },
        video: false
      };

      console.log('使用する制約:', constraints);

      // タイムアウト付きでマイクアクセスを取得
      const getMediaPromise = navigator.mediaDevices.getUserMedia(constraints);

      // モバイルブラウザではタイムアウトを設定
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => {
          reject(new Error('マイクアクセスのタイムアウト。ブラウザの許可ダイアログで「許可」を選択してください。'));
        }, 30000); // 30秒タイムアウト
      });

      this.localStream = await Promise.race([getMediaPromise, timeoutPromise]);

      console.log('マイクアクセスが許可されました！');
      console.log('ストリーム情報:', {
        audioTracks: this.localStream.getAudioTracks().length,
        videoTracks: this.localStream.getVideoTracks().length,
        active: this.localStream.active
      });

      // 音声レベル監視を開始
      this.startAudioLevelMonitoring();

      return true;
    } catch (error: any) {
      console.error('マイクアクセスの取得に失敗しました:', error);

      // エラーの詳細をログ出力
      if (error.name === 'NotAllowedError') {
        console.error('ユーザーによってマイクアクセスが拒否されました。');
      } else if (error.name === 'NotFoundError') {
        console.error('マイクデバイスが見つかりません。');
      } else if (error.name === 'NotReadableError') {
        console.error('マイクが他のアプリケーションで使用中です。');
      } else if (error.name === 'OverconstrainedError') {
        console.error('指定された制約を満たすマイクが見つかりません。');
      } else if (error.name === 'SecurityError') {
        console.error('セキュリティエラー。HTTPS接続が必要です。');
      } else if (error.name === 'AbortError') {
        console.error('マイクアクセスが中断されました。');
      }

      return false;
    }
  }

  async startVoiceChat(): Promise<void> {
    if (!this.targetUserId || !this.socket) {
      console.error('❌ ボイスチャット開始失敗: targetUserIdまたはsocketがありません');
      return;
    }

    // 重複開始の防止（より厳密なチェック）
    if (this.peerConnection) {
      const connectionState = this.peerConnection.connectionState;
      const signalingState = this.peerConnection.signalingState;
      
      if (connectionState !== 'closed' && connectionState !== 'failed') {
        console.warn('⚠️ 既にボイスチャットが開始されています。重複開始を防止します', {
          connectionState,
          signalingState
        });
        return;
      }
      
      // 古い接続をクリーンアップ
      if (connectionState === 'closed' || connectionState === 'failed') {
        console.log('🧹 古い接続をクリーンアップ中...');
        (this.peerConnection as RTCPeerConnection).close();
        this.peerConnection = null;
      }
    }

    try {
      console.log('🎤 ボイスチャット開始処理を開始...');

      // マイクアクセスを確認（既に初期化済みの場合）
      if (!this.localStream) {
        console.log('🔄 マイクアクセスを再初期化中...');
        const initialized = await this.initialize();
        if (!initialized) {
          console.error('❌ マイクアクセスの再初期化に失敗しました');
          return;
        }
      }

      // 音声トラックを確認
      if (!this.localStream) {
        console.error('❌ ローカルストリームが存在しません');
        return;
      }
      
      const audioTracks = this.localStream.getAudioTracks();
      if (audioTracks.length === 0) {
        console.error('❌ 音声トラックが見つかりません');
        return;
      }

      console.log('✅ マイクアクセス確認:', audioTracks[0].enabled, audioTracks[0].readyState);

      // PeerConnectionを作成
      this.createPeerConnection();

      // ローカルストリームを追加
      if (this.localStream && this.peerConnection) {
        console.log('📤 ローカルストリームをRTC接続に追加開始...');
        this.localStream.getTracks().forEach(track => {
          console.log('➕ ローカルトラックを追加:', track.kind, track.enabled, track.readyState);
          this.peerConnection!.addTrack(track, this.localStream!);
        });
        console.log('✅ ローカルストリーム追加完了:', this.peerConnection.getSenders().length, 'senders');
      } else {
        console.error('❌ ローカルストリームまたはpeerConnectionがありません');
      }

      // Offerを作成して送信
      const offer = await this.peerConnection!.createOffer();
      await this.peerConnection!.setLocalDescription(offer);

      this.socket.emit('webrtc-offer', {
        targetUserId: this.targetUserId,
        offer: offer
      });

      console.log('✅ ボイスチャット開始: Offer送信完了');
    } catch (error) {
      console.error('❌ ボイスチャット開始エラー:', error);
    }
  }

  private createPeerConnection(): void {
    this.peerConnection = new RTCPeerConnection(this.config);

    // リモートストリームの処理（超詳細版）
    this.peerConnection.ontrack = (event) => {
      console.log('🎧 リモート音声トラック受信！詳細情報:');
      console.log('トラック詳細:', {
        kind: event.track.kind,
        enabled: event.track.enabled,
        readyState: event.track.readyState,
        muted: event.track.muted,
        id: event.track.id,
        label: event.track.label
      });
      
      console.log('ストリーム詳細:', {
        streamCount: event.streams.length,
        audioTrackCount: event.streams[0]?.getAudioTracks().length || 0,
        videoTrackCount: event.streams[0]?.getVideoTracks().length || 0,
        active: event.streams[0]?.active,
        id: event.streams[0]?.id
      });

      this.remoteStream = event.streams[0];
      
      // リモートストリームの監視を開始
      if (this.remoteStream) {
        this.remoteStream.getAudioTracks().forEach((track, index) => {
          console.log(`🎵 リモート音声トラック${index}監視開始:`, {
            enabled: track.enabled,
            readyState: track.readyState,
            muted: track.muted
          });
          
          // トラック状態変更の監視
          track.onended = () => {
            console.warn(`⚠️ リモート音声トラック${index}が終了しました`);
          };
          
          track.onmute = () => {
            console.warn(`🔇 リモート音声トラック${index}がミュートされました`);
          };
          
          track.onunmute = () => {
            console.log(`🔊 リモート音声トラック${index}がアンミュートされました`);
          };
        });
      }
      
      this.onRemoteStream?.(this.remoteStream);
      console.log('✅ リモートストリーム受信完了');
    };

    // ICE candidateの処理
    this.peerConnection.onicecandidate = (event) => {
      if (event.candidate && this.socket && this.targetUserId) {
        this.socket.emit('webrtc-ice-candidate', {
          targetUserId: this.targetUserId,
          candidate: event.candidate
        });
      }
    };

    // 接続状態の監視（超詳細版）
    this.peerConnection.onconnectionstatechange = () => {
      const pc = this.peerConnection;
      if (!pc) {
        console.warn('⚠️ peerConnectionがnullです（既にクリーンアップ済み）');
        return;
      }
      
      const state = this.getConnectionState();
      this.onConnectionStateChange?.(state);
      
      // 詳細な状態ログ
      console.log('🔄 WebRTC接続状態変更:', {
        connectionState: pc.connectionState,
        iceConnectionState: pc.iceConnectionState,
        iceGatheringState: pc.iceGatheringState,
        signalingState: pc.signalingState,
        timestamp: new Date().toISOString()
      });

      // 接続確立時の詳細情報
      if (pc.connectionState === 'connected') {
        console.log('🎉 WebRTC接続確立！詳細情報:');
        console.log('📡 送信トラック数:', pc.getSenders().length);
        console.log('📡 受信トラック数:', pc.getReceivers().length);

        // 送信トラックの詳細
        pc.getSenders().forEach((sender, index) => {
            console.log(`📤 送信トラック${index}:`, {
              kind: sender.track?.kind,
              enabled: sender.track?.enabled,
              readyState: sender.track?.readyState,
              muted: sender.track?.muted
            });
          });

        // 受信トラックの詳細
        pc.getReceivers().forEach((receiver, index) => {
          console.log(`📡 受信トラック${index}:`, {
            kind: receiver.track?.kind,
            enabled: receiver.track?.enabled,
            readyState: receiver.track?.readyState,
            muted: receiver.track?.muted
          });
        });

        // 統計情報を即座に取得
        this.getDetailedStats();
      }

      // 接続失敗時の詳細ログと自動復旧
      if (pc.connectionState === 'failed') {
        console.error('❌ WebRTC接続失敗！詳細情報:');
        console.error('失敗時状態:', {
          connectionState: pc.connectionState,
          iceConnectionState: pc.iceConnectionState,
          iceGatheringState: pc.iceGatheringState,
          signalingState: pc.signalingState
        });
        this.getDetailedStats();
        console.warn('⚠️ 自動復旧を試行します...');
        this.handleConnectionFailure();
      }

      // 接続中に切断された場合
      if (pc.connectionState === 'disconnected') {
        console.warn('⚠️ WebRTC接続が切断されました。詳細情報:');
        this.getDetailedStats();
      }
    };

    // ICE接続状態の監視（超詳細版）
    this.peerConnection.oniceconnectionstatechange = () => {
      const pc = this.peerConnection;
      if (!pc) {
        console.warn('⚠️ peerConnectionがnullです（ICE状態変更）');
        return;
      }
      
      console.log('🧊 ICE接続状態変更:', {
        iceConnectionState: pc.iceConnectionState,
        connectionState: pc.connectionState,
        iceGatheringState: pc.iceGatheringState,
        timestamp: new Date().toISOString()
      });
        
      const state = this.getConnectionState();
      this.onConnectionStateChange?.(state);

      // ICE接続失敗時の詳細処理
      if (pc.iceConnectionState === 'failed') {
        console.error('❌ ICE接続失敗！詳細分析:');
        console.error('失敗時状態:', {
          iceConnectionState: pc.iceConnectionState,
          connectionState: pc.connectionState,
          iceGatheringState: pc.iceGatheringState,
          signalingState: pc.signalingState
        });
        
        // 統計情報を取得して失敗原因を分析
        this.getDetailedStats();
        
        console.warn('⚠️ ICE接続失敗のため、自動復旧を試行します...');
        this.handleConnectionFailure();
      } 
      // ICE接続切断時の詳細処理
      else if (pc.iceConnectionState === 'disconnected') {
        console.warn('⚠️ ICE接続が切断されました。詳細分析:');
        console.warn('切断時状態:', {
          iceConnectionState: pc.iceConnectionState,
          connectionState: pc.connectionState,
          iceGatheringState: pc.iceGatheringState,
          signalingState: pc.signalingState
        });
          
        // 統計情報を取得
        this.getDetailedStats();
        
        console.warn('⏳ 接続復旧を待機中...（5秒後にタイムアウト）');
        
        // 5秒後に復旧しない場合は再接続を試行
        setTimeout(() => {
          const currentPc = this.peerConnection;
          if (currentPc && currentPc.iceConnectionState === 'disconnected') {
            console.warn('⏰ 接続復旧タイムアウト。再接続を試行します');
            this.handleConnectionFailure();
          }
        }, 5000);
      }
      // ICE接続チェック中の詳細ログ
      else if (pc.iceConnectionState === 'checking') {
        console.log('🔍 ICE接続をチェック中...');
      }
      // ICE接続確立時の詳細ログ
      else if (pc.iceConnectionState === 'connected' || 
               pc.iceConnectionState === 'completed') {
        console.log('✅ ICE接続確立！');
        this.getDetailedStats();
      }
    };


    // シグナリング状態の監視（詳細版）
    this.peerConnection.onsignalingstatechange = () => {
      const pc = this.peerConnection;
      if (!pc) {
        console.warn('⚠️ peerConnectionがnullです（シグナリング状態変更）');
        return;
      }
      
      console.log('📡 シグナリング状態変更:', {
        signalingState: pc.signalingState,
        connectionState: pc.connectionState,
        iceConnectionState: pc.iceConnectionState,
        iceGatheringState: pc.iceGatheringState,
        timestamp: new Date().toISOString()
      });

      // シグナリング失敗時の処理
      if (pc.signalingState === 'closed') {
        console.warn('⚠️ シグナリング接続が閉じられました');
      }
    };

    // 音声品質監視を開始
    this.audioQualityMonitor = new AudioQualityMonitor(this.peerConnection);
    this.audioQualityMonitor.setOnQualityChange((quality) => {
      this.onQualityChange?.(quality);
    });
    this.audioQualityMonitor.setOnConnectionStats((stats) => {
      this.onConnectionStats?.(stats);
    });
    this.audioQualityMonitor.startMonitoring();

    // ハートビート監視を開始
    this.startHeartbeatMonitoring();
  }

  private async handleOffer(offer: RTCSessionDescriptionInit, fromUserId: string): Promise<void> {
    console.log('📨 Offerを受信しました:', fromUserId);
    
      // 既存の接続をクリーンアップ（SDP競合を防ぐ）
      if (this.peerConnection) {
        console.log('🔄 既存の接続をクリーンアップして新しい接続を作成');
        (this.peerConnection as RTCPeerConnection).close();
        this.peerConnection = null;
      }

    // 新しいPeerConnectionを作成
    this.createPeerConnection();

    try {
      // リモートディスクリプションを設定
        await this.peerConnection!.setRemoteDescription(offer);
        console.log('✅ リモートディスクリプション設定完了');
        
        // キューに保存されたICE候補を処理
        await this.processPendingIceCandidates();

      // Answerを作成して送信
      const answer = await this.peerConnection!.createAnswer();
      await this.peerConnection!.setLocalDescription(answer);
      console.log('✅ ローカルディスクリプション設定完了');

      if (this.socket) {
        this.socket.emit('webrtc-answer', {
          targetUserId: fromUserId,
          answer: answer
        });
      }

      console.log('📤 Answerを送信しました');
    } catch (error) {
      console.error('❌ Offer処理エラー:', error);
      
      // エラー時は接続をリセット
      if (this.peerConnection) {
        (this.peerConnection as RTCPeerConnection).close();
        this.peerConnection = null;
      }
    }
  }

  private async handleAnswer(answer: RTCSessionDescriptionInit): Promise<void> {
    if (this.peerConnection) {
      try {
        // 現在のシグナリング状態を確認
        console.log('📨 Answerを受信しました。現在の状態:', {
          signalingState: this.peerConnection.signalingState,
          connectionState: this.peerConnection.connectionState,
          iceConnectionState: this.peerConnection.iceConnectionState
        });
        
        // シグナリング状態をチェック
        if (this.peerConnection.signalingState !== 'have-local-offer') {
          console.warn('⚠️ 不正なシグナリング状態でAnswerを受信:', this.peerConnection.signalingState);
          
          // 接続をリセットして新しい接続を作成
          (this.peerConnection as RTCPeerConnection).close();
          this.peerConnection = null;
          
          // 新しい接続を作成
          this.createPeerConnection();
          
          // リモートディスクリプションを設定
          await this.peerConnection!.setRemoteDescription(answer);
          console.log('✅ Answer処理完了（新しい接続で）');
          
          // キューに保存されたICE候補を処理
          await this.processPendingIceCandidates();
          return;
        }
        
        await this.peerConnection.setRemoteDescription(answer);
        console.log('✅ Answer処理完了');
        
        // キューに保存されたICE候補を処理
        await this.processPendingIceCandidates();
      } catch (error) {
        console.error('❌ Answer処理エラー:', error);
        
        // エラー時は接続をリセット
        if (this.peerConnection) {
          (this.peerConnection as RTCPeerConnection).close();
          this.peerConnection = null;
        }
      }
    } else {
      console.warn('⚠️ PeerConnectionが存在しません。Answerを無視します');
    }
  }

  private async handleIceCandidate(candidate: RTCIceCandidateInit): Promise<void> {
    if (this.peerConnection) {
      try {
        // 接続状態をチェック
        const connectionState = this.peerConnection.connectionState;
        const signalingState = this.peerConnection.signalingState;
        
        if (connectionState === 'closed' || connectionState === 'failed') {
          console.warn('⚠️ 接続が終了済みです。ICE candidateを無視します', {
            connectionState,
            signalingState
          });
          return;
        }
        
        // リモートディスクリプションが設定されているか確認
        if (!this.peerConnection.remoteDescription) {
          console.warn('⚠️ リモートディスクリプションが未設定です。ICE candidateをキューに保存');
          
          // ICE candidateをキューに保存（後で処理）
          if (!this.pendingIceCandidates) {
            this.pendingIceCandidates = [];
          }
          this.pendingIceCandidates.push(candidate);
          return;
        }
        
        await this.peerConnection.addIceCandidate(candidate);
        console.log('✅ ICE candidateを追加しました');
      } catch (error) {
        console.error('❌ ICE candidate処理エラー:', error);
        
        // 特定のエラーは無視（既に追加済みなど）
        if (error instanceof Error && error.name === 'OperationError' && error.message.includes('already exists')) {
          console.log('ℹ️ ICE candidateは既に追加済みです');
        }
      }
    } else {
      console.warn('⚠️ PeerConnectionが存在しません。ICE candidateを無視します');
    }
  }

  private startAudioLevelMonitoring(): void {
    if (!this.localStream) {
      return;
    }

    // AudioQualityMonitorの音声レベル監視機能を使用
    this.audioLevelMonitor = AudioQualityMonitor.createAudioLevelMonitor(
      this.localStream,
      (level) => {
        const isSpeaking = level > 0.1; // 閾値は調整可能

        if (this.onConnectionStateChange) {
          const currentState = this.getConnectionState();
          currentState.isSpeaking = isSpeaking;
          this.onConnectionStateChange(currentState);
        }
      }
    );
  }

  // 接続維持のための強化されたハートビート監視
  private startHeartbeatMonitoring(): void {
    if (!this.peerConnection) {
      return;
    }

    let consecutiveFailures = 0;
    const maxFailures = 3;

    const heartbeatInterval = setInterval(async () => {
      if (!this.peerConnection) {
        clearInterval(heartbeatInterval);
        return;
      }

      try {
        console.log('💓 ハートビート監視実行中...');
        
        // 接続状態を詳細確認
        const state = this.getConnectionState();
        console.log('💓 現在の接続状態:', {
          isConnected: state.isConnected,
          connectionQuality: state.connectionQuality,
          iceConnectionState: this.peerConnection.iceConnectionState,
          connectionState: this.peerConnection.connectionState
        });
        
        // 接続が不安定な場合の処理
        if (state.connectionQuality === 'poor' || !state.isConnected) {
          consecutiveFailures++;
          console.warn(`⚠️ 接続品質が低下しています (${consecutiveFailures}/${maxFailures})。診断を実行します...`);
          
          // 詳細統計を取得
          await this.getDetailedStats();
          
          const diagnosis = await this.diagnoseConnection();
          if (!diagnosis.isHealthy) {
            console.warn('🔍 接続問題を検出:', diagnosis.issues);
            console.log('💡 推奨事項:', diagnosis.recommendations);
            
            // 重大な問題がある場合は自動復旧を試行
            if (diagnosis.issues.some(issue => 
              issue.includes('失敗') || issue.includes('切断')
            ) || consecutiveFailures >= maxFailures) {
              console.error(`❌ 接続失敗が${consecutiveFailures}回連続発生。自動復旧を実行します`);
              this.handleConnectionFailure();
              consecutiveFailures = 0; // リセット
            }
          } else {
            console.log('✅ 診断結果: 接続は正常です');
            consecutiveFailures = 0; // 正常時はリセット
          }
        } else {
          // 接続が正常な場合
          consecutiveFailures = 0;
          console.log('✅ 接続状態は正常です');
          
          // 定期的に統計情報を取得（デバッグ用）
          if (Math.random() < 0.3) { // 30%の確率で実行
            await this.getDetailedStats();
          }
        }
      } catch (error) {
        console.error('ハートビート監視エラー:', error);
        consecutiveFailures++;
        
        if (consecutiveFailures >= maxFailures) {
          console.error('❌ ハートビート監視が連続で失敗。接続をリセットします');
          this.handleConnectionFailure();
          consecutiveFailures = 0;
        }
      }
    }, 5000); // 5秒ごとにチェック（より頻繁に）

    // クリーンアップ用の参照を保存
    (this as any).heartbeatInterval = heartbeatInterval;
  }

  private stopHeartbeatMonitoring(): void {
    if ((this as any).heartbeatInterval) {
      clearInterval((this as any).heartbeatInterval);
      (this as any).heartbeatInterval = null;
    }
  }

  // キューに保存されたICE候補を処理
  private async processPendingIceCandidates(): Promise<void> {
    if (!this.pendingIceCandidates || this.pendingIceCandidates.length === 0) {
      return;
    }

    console.log(`🔄 ${this.pendingIceCandidates.length}個の保留中のICE候補を処理中...`);
    
    for (const candidate of this.pendingIceCandidates) {
      try {
        await this.peerConnection!.addIceCandidate(candidate);
        console.log('✅ 保留中のICE candidateを追加しました');
      } catch (error) {
        console.error('❌ 保留中のICE candidate処理エラー:', error);
      }
    }
    
    // キューをクリア
    this.pendingIceCandidates = [];
    console.log('✅ 保留中のICE候補の処理が完了しました');
  }

  private getConnectionState(): VoiceChatState {
    if (!this.peerConnection) {
      return {
        isConnected: false,
        isMuted: !this.localStream?.getAudioTracks()[0]?.enabled,
        isSpeaking: false,
        connectionQuality: 'disconnected'
      };
    }

    let quality: 'excellent' | 'good' | 'poor' | 'disconnected' = 'disconnected';
    
    switch (this.peerConnection.iceConnectionState) {
      case 'connected':
      case 'completed':
        quality = 'excellent';
        break;
      case 'checking':
        quality = 'good';
        break;
      case 'disconnected':
      case 'failed':
        quality = 'poor';
        break;
    }

    // より厳密な接続状態判定
    const isConnected = this.peerConnection.connectionState === 'connected' && 
                       (this.peerConnection.iceConnectionState === 'connected' || 
                        this.peerConnection.iceConnectionState === 'completed');

    return {
      isConnected: isConnected,
      isMuted: !this.localStream?.getAudioTracks()[0]?.enabled,
      isSpeaking: false, // これは音声レベル監視で更新される
      connectionQuality: isConnected ? quality : 'disconnected'
    };
  }

  // ミュート/アンミュート切り替え
  toggleMute(): boolean {
    if (!this.localStream) {
      return false;
    }

    const audioTrack = this.localStream.getAudioTracks()[0];
    if (audioTrack) {
      audioTrack.enabled = !audioTrack.enabled;
      
      // 相手に音声状態を通知
      if (this.socket && this.targetUserId) {
        this.socket.emit('audio-state-change', {
          targetUserId: this.targetUserId,
          isMuted: !audioTrack.enabled
        });
      }

      return audioTrack.enabled;
    }
    return false;
  }

  // ボイスチャット終了
  endVoiceChat(): void {
    console.log('🛑 ボイスチャット終了処理を開始...');
    
    // ハートビート監視を停止
    this.stopHeartbeatMonitoring();

    if (this.audioQualityMonitor) {
      this.audioQualityMonitor.stopMonitoring();
      this.audioQualityMonitor = null;
    }

    if (this.audioLevelMonitor) {
      this.audioLevelMonitor();
      this.audioLevelMonitor = null;
    }

    if (this.peerConnection) {
      console.log('🔄 PeerConnectionをクローズ中...');
      (this.peerConnection as RTCPeerConnection).close();
      this.peerConnection = null;
    }

    if (this.localStream) {
      console.log('🔄 ローカルストリームを停止中...');
      this.localStream.getTracks().forEach(track => track.stop());
      this.localStream = null;
    }

    if (this.remoteStream) {
      this.remoteStream = null;
    }

    // 保留中のICE候補をクリア
    this.pendingIceCandidates = null;

    if (this.socket && this.roomId) {
      this.socket.emit('end-voice-chat', { roomId: this.roomId });
    }

    this.roomId = null;
    this.targetUserId = null;

    console.log('✅ ボイスチャット終了処理完了');
  }

  // コールバック関数の設定
  setOnConnectionStateChange(callback: (state: VoiceChatState) => void): void {
    this.onConnectionStateChange = callback;
  }

  setOnRemoteStream(callback: (stream: MediaStream) => void): void {
    this.onRemoteStream = callback;
  }

  setOnAudioStateChange(callback: (isMuted: boolean) => void): void {
    this.onAudioStateChange = callback;
  }

  setOnQualityChange(callback: (quality: AudioQualityMetrics) => void): void {
    this.onQualityChange = callback;
  }

  setOnConnectionStats(callback: (stats: ConnectionStats) => void): void {
    this.onConnectionStats = callback;
  }

  // 詳細統計情報取得
  private async getDetailedStats(): Promise<void> {
    if (!this.peerConnection) {
      return;
    }

    try {
      const stats = await this.peerConnection.getStats();
      console.log('📊 詳細統計情報:');
      
      stats.forEach((report) => {
        if (report.type === 'inbound-rtp' && report.mediaType === 'audio') {
          console.log('📥 受信音声統計:', {
            bytesReceived: report.bytesReceived,
            packetsReceived: report.packetsReceived,
            packetsLost: report.packetsLost,
            jitter: report.jitter,
            ssrc: report.ssrc,
            timestamp: report.timestamp
          });
        } else if (report.type === 'outbound-rtp' && report.mediaType === 'audio') {
          console.log('📤 送信音声統計:', {
            bytesSent: report.bytesSent,
            packetsSent: report.packetsSent,
            ssrc: report.ssrc,
            timestamp: report.timestamp
          });
        } else if (report.type === 'candidate-pair' && report.state === 'succeeded') {
          console.log('🔗 接続ペア統計:', {
            state: report.state,
            currentRoundTripTime: report.currentRoundTripTime,
            availableOutgoingBitrate: report.availableOutgoingBitrate,
            availableIncomingBitrate: report.availableIncomingBitrate,
            timestamp: report.timestamp
          });
        } else if (report.type === 'remote-inbound-rtp' && report.mediaType === 'audio') {
          console.log('📡 リモート受信統計:', {
            packetsLost: report.packetsLost,
            jitter: report.jitter,
            roundTripTime: report.roundTripTime,
            timestamp: report.timestamp
          });
        }
      });
    } catch (error) {
      console.error('統計情報取得エラー:', error);
    }
  }

  // 接続失敗時の自動復旧処理
  private handleConnectionFailure(): void {
    console.log('🔄 接続失敗を検出。自動復旧を開始します...');
    
    // 既存の接続をクリーンアップ
    if (this.peerConnection) {
      this.peerConnection.close();
      this.peerConnection = null;
    }

    // 3秒後に再接続を試行
    setTimeout(() => {
      if (this.targetUserId && this.socket) {
        console.log('🔄 自動復旧: ボイスチャットを再開始します');
        this.startVoiceChat();
      }
    }, 3000);
  }

  // 接続状態の詳細診断
  async diagnoseConnection(): Promise<{
    isHealthy: boolean;
    issues: string[];
    recommendations: string[];
  }> {
    const issues: string[] = [];
    const recommendations: string[] = [];

    if (!this.peerConnection) {
      issues.push('PeerConnectionが存在しません');
      recommendations.push('WebRTC接続を再初期化してください');
      return { isHealthy: false, issues, recommendations };
    }

    // 接続状態の確認
    if (this.peerConnection.connectionState === 'failed') {
      issues.push('WebRTC接続が失敗状態です');
      recommendations.push('ページをリロードして再接続してください');
    }

    if (this.peerConnection.iceConnectionState === 'failed') {
      issues.push('ICE接続が失敗状態です');
      recommendations.push('ネットワーク設定を確認してください');
    }

    if (this.peerConnection.iceConnectionState === 'disconnected') {
      issues.push('ICE接続が切断されています');
      recommendations.push('ネットワーク接続を確認してください');
    }

    // 統計情報の確認
    try {
      const stats = await this.peerConnection.getStats();
      let highPacketLoss = false;
      let highLatency = false;

      stats.forEach((report) => {
        if (report.type === 'inbound-rtp' && report.mediaType === 'audio') {
          const packetLoss = report.packetsLost || 0;
          const packetsReceived = report.packetsReceived || 0;
          const lossRate = packetsReceived > 0 ? (packetLoss / (packetsReceived + packetLoss)) * 100 : 0;
          
          if (lossRate > 5) {
            highPacketLoss = true;
            issues.push(`高いパケット損失率: ${lossRate.toFixed(1)}%`);
          }
        }
        
        if (report.type === 'candidate-pair' && report.state === 'succeeded') {
          const rtt = report.currentRoundTripTime || 0;
          if (rtt > 0.5) { // 500ms以上
            highLatency = true;
            issues.push(`高い遅延: ${(rtt * 1000).toFixed(0)}ms`);
          }
        }
      });

      if (highPacketLoss) {
        recommendations.push('より安定したネットワーク環境に移動してください');
      }

      if (highLatency) {
        recommendations.push('より高速なネットワーク接続を使用してください');
      }
    } catch (error) {
      console.error('統計情報取得エラー:', error);
    }

    return {
      isHealthy: issues.length === 0,
      issues,
      recommendations
    };
  }

  // リソースのクリーンアップ
  cleanup(): void {
    this.endVoiceChat();
    this.socket = null;
    this.currentUserId = null;
  }

  // ゲッター
  getLocalStream(): MediaStream | null {
    return this.localStream;
  }

  getRemoteStream(): MediaStream | null {
    return this.remoteStream;
  }

  getCurrentState(): VoiceChatState {
    return this.getConnectionState();
  }
}
